import { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem } from '@/components/shadcn/form'
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/radio-group'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { Badge } from '@/components/shadcn/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Label } from '@/components/shadcn/label'

import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { AppSettingsAction, AppSettingsType } from '@/util/appSettingsEnums'
import { Loading } from '@/ui-legacy'
import { toast } from 'sonner'
import { OrgMappingSection } from './OrgMappingSection'

type MessagesAppSettingsAppSettingsResultItem = RestSchemas['Messages.AppSettings.AppSettingsResultItem']

// Action type for the form
const actionSchema = z.enum(['0', '2', '3'])

const orgMappingSchema = z.object({ sourceName: z.string(), destinationName: z.string() })

// Form validation schema
const formSchema = z.object({
  configSettingsAction: actionSchema,
  rolesAction: actionSchema,
  teamsAction: actionSchema,
  teamRolesAction: actionSchema,
  entityTypesAction: actionSchema,
  fileTemplatesAction: actionSchema,
  fileTemplateRolesAction: actionSchema,
  glossaryAction: actionSchema,
  faqAction: actionSchema,
  dataAssetsAction: actionSchema,
  dataAssetPermissionsAction: actionSchema,
  collaborationChannelsAction: actionSchema,
  collaborationChannelRolesAction: actionSchema,
  pipelinesAction: actionSchema,
  pipelinePermissionsAction: actionSchema,
  customMenusAction: actionSchema,
  organisationMappings: z.array(orgMappingSchema),
})

export type TSyncImportForm = z.infer<typeof formSchema>

type SyncImportFormProps = {
  file: File
}

// Helper function to get item resolution based on action and status
const getItemResolution = (action: string, status: string): string => {
  const actionNum = parseInt(action)
  if (actionNum === AppSettingsAction.None && status === 'extra') return 'Remain'
  if (actionNum === AppSettingsAction.None && status === 'exists') return 'Remain'
  if (actionNum === AppSettingsAction.None && status === 'new') return 'Skip'
  if (actionNum === AppSettingsAction.Merge && status === 'extra') return 'Remain'
  if (actionNum === AppSettingsAction.Merge && status === 'exists') return 'Update'
  if (actionNum === AppSettingsAction.Merge && status === 'new') return 'Add'
  if (actionNum === AppSettingsAction.Replace && status === 'extra') return 'Delete'
  if (actionNum === AppSettingsAction.Replace && status === 'exists') return 'Update'
  if (actionNum === AppSettingsAction.Replace && status === 'new') return 'Add'
  return 'No Action'
}

// Helper function to get badge variant for status
const getStatusBadgeVariant = (status: string): 'warning' | 'success' | 'warning' => {
  switch (status) {
    case 'new':
      return 'warning'
    case 'exists':
      return 'success'
    case 'extra':
      return 'warning'
    default:
      return 'warning'
  }
}

// Helper function to get error message from unknown error type
const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') return error
  if (error instanceof Error) return error.message
  return 'An error occurred while applying settings'
}

// Helper function to get badge variant for action resolution
const getActionBadgeVariant = (resolution: string): 'secondary' | 'success' | 'warning' | 'destructive' => {
  switch (resolution) {
    case 'Skip':
    case 'Remain':
      return 'secondary'
    case 'Add':
      return 'success'
    case 'Update':
      return 'warning'
    case 'Delete':
      return 'destructive'
    default:
      return 'secondary'
  }
}

type TActionField = Exclude<keyof TSyncImportForm, 'organisationMappings'>

type SyncSectionConfig = {
  title: string
  fieldName: TActionField
  type: string
}

const SYNC_SECTIONS: SyncSectionConfig[] = [
  { title: 'Config Settings', fieldName: 'configSettingsAction', type: 'configSettings' },
  { title: 'Roles', fieldName: 'rolesAction', type: 'roles' },
  { title: 'Teams', fieldName: 'teamsAction', type: 'teams' },
  { title: 'Team Roles', fieldName: 'teamRolesAction', type: 'teamRoles' },
  { title: 'Entity Types', fieldName: 'entityTypesAction', type: 'entityTypes' },
  { title: 'File Templates', fieldName: 'fileTemplatesAction', type: 'fileTemplates' },
  { title: 'File Template Roles', fieldName: 'fileTemplateRolesAction', type: 'fileTemplateRoles' },
  { title: 'Glossary', fieldName: 'glossaryAction', type: 'glossary' },
  { title: 'FAQ', fieldName: 'faqAction', type: 'faq' },
  { title: 'Data assets', fieldName: 'dataAssetsAction', type: 'dataAssets' },
  { title: 'Data Asset Permissions', fieldName: 'dataAssetPermissionsAction', type: 'dataAssetPermissions' },
  { title: 'Collaboration Channels', fieldName: 'collaborationChannelsAction', type: 'collaborationChannels' },
  {
    title: 'Collaboration Channel Roles',
    fieldName: 'collaborationChannelRolesAction',
    type: 'collaborationChannelRoles',
  },
  { title: 'Pipelines', fieldName: 'pipelinesAction', type: 'pipelines' },
  { title: 'Pipeline Permissions', fieldName: 'pipelinePermissionsAction', type: 'pipelinePermissions' },
  { title: 'Custom Menus', fieldName: 'customMenusAction', type: 'customMenus' },
]

// Sync type block component
type SyncTypeBlockProps = {
  title: string
  items: MessagesAppSettingsAppSettingsResultItem[]
  fieldName: TActionField
  control: ReturnType<typeof useForm<TSyncImportForm>>['control']
  watchValue: string
}

const SyncTypeBlock = ({ title, items, fieldName, control, watchValue }: SyncTypeBlockProps) => {
  if (items.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No items in current configuration or uploaded file</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
          <FormField
            control={control}
            name={fieldName}
            render={({ field }) => (
              <FormItem className="space-y-0">
                <FormControl>
                  <RadioGroup onValueChange={field.onChange} value={field.value} className="flex flex-row gap-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="0" id={`${fieldName}-skip`} />
                      <Label htmlFor={`${fieldName}-skip`} className="cursor-pointer text-sm font-normal">
                        Skip
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="2" id={`${fieldName}-merge`} />
                      <Label htmlFor={`${fieldName}-merge`} className="cursor-pointer text-sm font-normal">
                        Merge
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="3" id={`${fieldName}-replace`} />
                      <Label htmlFor={`${fieldName}-replace`} className="cursor-pointer text-sm font-normal">
                        Replace
                      </Label>
                    </div>
                  </RadioGroup>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead className="w-[100px]">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const status = String(item.status ?? '')
              const resolution = getItemResolution(watchValue, status)
              return (
                <TableRow key={`${String(item.type)}-${item.elementName}`}>
                  <TableCell className="font-medium">{item.elementName}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(status)}>{status}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getActionBadgeVariant(resolution)}>{resolution}</Badge>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

export function SyncImportForm({ file }: SyncImportFormProps) {
  const {
    postAppSettingsApiAppSettingsUpload,
    getAppSettingsApiGetAppSettingsUpload,
    patchAppSettingsApiGetAppSettingsUpload,
  } = useRestClient()

  // Upload file mutation
  const {
    mutate: uploadFile,
    data: appSettingsUploadId,
    isPending: isUploading,
  } = postAppSettingsApiAppSettingsUpload.useMutation()

  // Apply settings mutation
  const {
    mutate: applySettings,
    isPending: isApplying,
    isSuccess: applySuccess,
    error: applyError,
  } = patchAppSettingsApiGetAppSettingsUpload.useMutation(undefined, {
    onSuccess: () => {
      toast.success('Settings successfully applied')
    },
    onError: () => {
      toast.error('Error applying settings')
    },
  })

  // Fetch upload results query
  const { data: appSettingsUpload, isLoading: isLoadingResults } = getAppSettingsApiGetAppSettingsUpload.useQuery(
    { path: { appSettingsUploadId: appSettingsUploadId as unknown as number } },
    { enabled: !!appSettingsUploadId }
  )

  // Upload file on mount
  useEffect(() => {
    if (file) {
      uploadFile({ body: { file } })
    }
  }, [file, uploadFile])

  // Categorize items by type
  const categorizedItems = useMemo(() => {
    const result: Record<string, MessagesAppSettingsAppSettingsResultItem[]> = {
      app: [],
      configSettings: [],
      roles: [],
      teams: [],
      organisation: [],
      teamRoles: [],
      entityTypes: [],
      fileTemplates: [],
      fileTemplateRoles: [],
      glossary: [],
      faq: [],
      dataAssets: [],
      dataAssetPermissions: [],
      collaborationChannels: [],
      collaborationChannelRoles: [],
      pipelines: [],
      pipelinePermissions: [],
      customMenus: [],
    }

    appSettingsUpload?.checkResults?.forEach((item) => {
      const type = String(item.type ?? '')
      if (result[type]) {
        result[type].push(item)
      }
    })

    return result
  }, [appSettingsUpload])

  const defaultValues = useMemo(
    () => ({
      configSettingsAction: '0' as const,
      rolesAction: '0' as const,
      teamsAction: '0' as const,
      teamRolesAction: '0' as const,
      entityTypesAction: '0' as const,
      fileTemplatesAction: '0' as const,
      fileTemplateRolesAction: '0' as const,
      glossaryAction: '0' as const,
      faqAction: '0' as const,
      dataAssetsAction: '0' as const,
      dataAssetPermissionsAction: '0' as const,
      collaborationChannelsAction: '0' as const,
      collaborationChannelRolesAction: '0' as const,
      pipelinesAction: '0' as const,
      pipelinePermissionsAction: '0' as const,
      customMenusAction: '0' as const,
      organisationMappings: [] as { sourceName: string; destinationName: string }[],
    }),
    []
  )

  const form = useForm<TSyncImportForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const watchedValues = form.watch()

  // Set all actions at once (organisations handled separately via mappings)
  const setAllActions = useCallback(
    (action: '0' | '2' | '3') => {
      const fields: TActionField[] = [
        'configSettingsAction',
        'rolesAction',
        'teamsAction',
        'teamRolesAction',
        'entityTypesAction',
        'fileTemplatesAction',
        'fileTemplateRolesAction',
        'glossaryAction',
        'faqAction',
        'dataAssetsAction',
        'dataAssetPermissionsAction',
        'collaborationChannelsAction',
        'collaborationChannelRolesAction',
        'pipelinesAction',
        'pipelinePermissionsAction',
        'customMenusAction',
      ]
      fields.forEach((field) => form.setValue(field, action))
    },
    [form]
  )

  const submitHandler = useCallback(
    (data: TSyncImportForm) => {
      if (!appSettingsUploadId) return

      applySettings({
        path: { appSettingsUploadId: appSettingsUploadId as unknown as number },
        body: [
          { operationType: 2, path: '/status', op: 'replace', from: null, value: 2 },
          {
            operationType: 2,
            path: '/applyRequest',
            op: 'replace',
            from: null,
            value: {
              organisationMappings: data.organisationMappings.map((m) => ({
                sourceName: m.sourceName,
                // sentinel means "create new org using source name"
                destinationName: m.destinationName === '__create_new__' ? m.sourceName : m.destinationName,
              })),
              items: [
                { type: AppSettingsType.App, action: AppSettingsAction.Merge },
                { type: AppSettingsType.ConfigSettings, action: parseInt(data.configSettingsAction) },
                { type: AppSettingsType.Roles, action: parseInt(data.rolesAction) },
                { type: AppSettingsType.Teams, action: parseInt(data.teamsAction) },
                { type: AppSettingsType.Organisation, action: AppSettingsAction.Merge },
                { type: AppSettingsType.TeamRoles, action: parseInt(data.teamRolesAction) },
                { type: AppSettingsType.EntityTypes, action: parseInt(data.entityTypesAction) },
                { type: AppSettingsType.FileTemplates, action: parseInt(data.fileTemplatesAction) },
                { type: AppSettingsType.FileTemplateRoles, action: parseInt(data.fileTemplateRolesAction) },
                { type: AppSettingsType.Glossary, action: parseInt(data.glossaryAction) },
                { type: AppSettingsType.Faq, action: parseInt(data.faqAction) },
                { type: AppSettingsType.DataAssets, action: parseInt(data.dataAssetsAction) },
                { type: AppSettingsType.DataAssetPermissions, action: parseInt(data.dataAssetPermissionsAction) },
                // Only include collaboration items if they have data
                ...(categorizedItems.collaborationChannels.length > 0
                  ? [
                      {
                        type: AppSettingsType.CollaborationChannels,
                        action: parseInt(data.collaborationChannelsAction),
                      },
                    ]
                  : []),
                ...(categorizedItems.collaborationChannelRoles.length > 0
                  ? [
                      {
                        type: AppSettingsType.CollaborationChannelRoles,
                        action: parseInt(data.collaborationChannelRolesAction),
                      },
                    ]
                  : []),
                ...(categorizedItems.pipelines.length > 0
                  ? [{ type: AppSettingsType.Pipelines, action: parseInt(data.pipelinesAction) }]
                  : []),
                ...(categorizedItems.pipelinePermissions.length > 0
                  ? [{ type: AppSettingsType.PipelinePermissions, action: parseInt(data.pipelinePermissionsAction) }]
                  : []),
                ...(categorizedItems.customMenus.length > 0
                  ? [{ type: AppSettingsType.CustomMenus, action: parseInt(data.customMenusAction) }]
                  : []),
              ],
            },
          },
        ],
      })
    },
    [appSettingsUploadId, applySettings, categorizedItems]
  )

  // Loading states
  if (isUploading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <Loading />
        <p className="text-sm text-muted-foreground">Uploading file...</p>
      </div>
    )
  }

  if (isLoadingResults) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <Loading />
        <p className="text-sm text-muted-foreground">Analyzing configuration...</p>
      </div>
    )
  }

  const appItem = categorizedItems.app[0]

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submitHandler)}>
        {/* Header section with file info and global actions */}
        <div className="sticky top-[57px] z-10 mb-6 flex flex-col gap-4 rounded-lg bg-offwhite/80 p-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
          <div>
            {appItem && (
              <p className="mt-1 text-sm">
                Application: <span className="font-semibold">{appItem.elementName}</span>
              </p>
            )}
            <p className="text-sm font-medium">
              <span>
                File: <span className="font-semibold">{file.name}</span>
              </span>
              <span className="ml-2 text-xs text-muted-foreground">{`(${(file.size / 1024).toFixed(1)} KB)`}</span>
            </p>
          </div>
          <div className="flex flex-col gap-10 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Set all:</span>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setAllActions('0')}>
                  Skip
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setAllActions('2')}>
                  Merge
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setAllActions('3')}>
                  Replace
                </Button>
              </div>
            </div>
            <Button type="submit" size="sm" disabled={isApplying || !appSettingsUploadId}>
              {isApplying ? 'Applying...' : 'Apply'}
            </Button>
          </div>
        </div>

        {/* Success/Error messages */}
        {applySuccess === true && (
          <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800">
            Successfully applied configuration
          </div>
        )}
        {applyError != null && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
            {getErrorMessage(applyError)}
          </div>
        )}

        {/* Grid of sync sections */}
        <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-6">
          <OrgMappingSection
            items={categorizedItems.organisation}
            availableOrgs={appSettingsUpload?.availableOrganisations ?? []}
            value={watchedValues.organisationMappings ?? []}
            onChange={(mappings) => form.setValue('organisationMappings', mappings)}
          />
          {SYNC_SECTIONS.map((section) => (
            <SyncTypeBlock
              key={section.fieldName}
              title={section.title}
              items={categorizedItems[section.type] ?? []}
              fieldName={section.fieldName}
              control={form.control}
              watchValue={watchedValues[section.fieldName] as string}
            />
          ))}
        </div>
      </form>
    </Form>
  )
}
